/**
 * Rejection Status Page — fully localized via provider.rejectionStatus.*
 * Business rules unchanged:
 *  - ONLY flagged fields are editable
 *  - All other fields are strictly read-only
 *  - On resubmission: status becomes REAPPLIED
 *  - Version history is preserved
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useClinicApplication,
  ClinicApplicationProvider,
  APPLICATION_STATUS,
  FLAGGABLE_FIELDS,
  COUNTRIES,
} from '../../context/ClinicApplicationContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  XCircle, AlertTriangle, FileText, Calendar, User,
  ArrowRight, CheckCircle, Upload, RefreshCw, Lock, Edit2,
  Building2, MapPin, Shield, History,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';

// Country display keeps the flag glyph and the reviewer-authored proper
// noun (`country.name`); those are canonical/authored content and are NOT
// translated per §2. Only the system label around the missing state is.
const getCountryDisplay = (code, notSpecified) => {
  const country = COUNTRIES.find((c) => c.code === code);
  return country ? `${country.flag} ${country.name}` : (code || notSpecified);
};

// Human-readable status label — surrounding label localized, backend
// enum value canonical (per §2).
const statusLabelKey = (backendStatus) => {
  switch ((backendStatus || '').toLowerCase()) {
    case 'pending_review':
    case 'pending':
      return 'provider.rejectionStatus.statusPending';
    case 'approved':
      return 'provider.rejectionStatus.statusApproved';
    case 'reapplied':
      return 'provider.rejectionStatus.statusReapplied';
    default:
      return null;
  }
};

const RejectionStatusPageInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id');

  const {
    getApplicationById,
    reapplyApplication,
    SPECIALIZATIONS,
  } = useClinicApplication();

  const getSpecializationLabel = (id) => {
    const spec = (SPECIALIZATIONS || []).find((s) => s.id === id);
    return spec ? spec.label : id;
  };

  // Locale-neutral display label for FLAGGABLE_FIELDS keys. Falls back to
  // the backend/context label if no i18n key is provisioned.
  const fieldLabel = (fieldKey) => {
    const key = `provider.rejectionStatus.field_${fieldKey}`;
    const localized = t(key);
    if (localized && localized !== key) return localized;
    return FLAGGABLE_FIELDS[fieldKey]?.label || fieldKey;
  };

  const [application, setApplication] = useState(null);
  const [isReapplying, setIsReapplying] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (applicationId) {
      const app = getApplicationById(applicationId);
      if (app) {
        setApplication(app);
        const initialEdits = {};
        (app.flaggedFields || []).forEach((field) => {
          if (field === 'businessLicense' || field === 'medicalCertifications' || field === 'additionalDocs') {
            if (!initialEdits.documents) {
              initialEdits.documents = { ...app.documents };
            }
          } else {
            initialEdits[field] = app[field];
          }
        });
        setEditedFields(initialEdits);
      }
    }
  }, [applicationId, getApplicationById]);

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Toaster />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('provider.rejectionStatus.notFoundTitle')}</h2>
            <p className="text-gray-600 mb-4">{t('provider.rejectionStatus.notFoundBody')}</p>
            <Button onClick={() => navigate('/clinic/application-status')} data-testid="check-status-btn">
              {t('provider.rejectionStatus.notFoundCta')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (application.status !== APPLICATION_STATUS.REJECTED) {
    const rawStatus = String(application.status || '').toLowerCase().replace('_', ' ');
    const sKey = statusLabelKey(application.status);
    const statusText = sKey ? t(sKey) : rawStatus;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Toaster />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('provider.rejectionStatus.notRejectedTitle')}</h2>
            <p className="text-gray-600 mb-4">
              {t('provider.rejectionStatus.notRejectedBody', { status: statusText })}
            </p>
            <Button onClick={() => navigate('/clinic/application-status?email=' + encodeURIComponent(application.email))} data-testid="view-status-btn">
              {t('provider.rejectionStatus.notRejectedCta')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const flaggedFields = application.flaggedFields || [];

  const hasChanges = Object.keys(editedFields).some((key) => {
    if (key === 'documents') {
      return JSON.stringify(application.documents) !== JSON.stringify(editedFields.documents);
    }
    if (key === 'specializations') {
      return JSON.stringify(application[key]) !== JSON.stringify(editedFields[key]);
    }
    return application[key] !== editedFields[key];
  });

  const handleFieldChange = (field, value) => {
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocumentUpload = (docType, file) => {
    const mockUrl = URL.createObjectURL(file);
    setEditedFields((prev) => ({
      ...prev,
      documents: {
        ...(prev.documents || application.documents),
        [docType]: {
          name: file.name,
          url: mockUrl,
          uploaded: true,
          uploadedAt: new Date().toISOString(),
        },
      },
    }));
    toast({
      title: t('provider.rejectionStatus.docUploadedTitle'),
      description: t('provider.rejectionStatus.docUploadedDesc', { name: file.name }),
    });
  };

  const handleReapply = async () => {
    if (!hasChanges) {
      toast({
        title: t('provider.rejectionStatus.noChangesTitle'),
        description: t('provider.rejectionStatus.noChangesDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const success = reapplyApplication(application.id, editedFields);

      if (success) {
        toast({
          title: t('provider.rejectionStatus.submitted'),
          description: t('provider.rejectionStatus.submittedDesc'),
        });
        setTimeout(() => {
          navigate('/clinic/application-status?email=' + encodeURIComponent(application.email));
        }, 1500);
      } else {
        throw new Error('reapply failed');
      }
    } catch (error) {
      toast({
        title: t('provider.rejectionStatus.submitFailed'),
        description: t('provider.rejectionStatus.submitFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (fieldKey) => {
    const fieldInfo = FLAGGABLE_FIELDS[fieldKey];
    if (!fieldInfo) return null;

    const isFlagged = flaggedFields.includes(fieldKey);
    const currentValue = editedFields[fieldKey] ?? application[fieldKey];
    const label = fieldLabel(fieldKey);

    if (fieldInfo.category === 'documents') {
      const docType = fieldKey;
      const currentDoc = editedFields.documents?.[docType] || application.documents?.[docType];

      return (
        <div
          key={fieldKey}
          className={`p-4 rounded-lg border-2 transition-all ${isFlagged ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
          data-testid={`field-${fieldKey}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className={`h-4 w-4 ${isFlagged ? 'text-red-600' : 'text-gray-500'}`} />
              <span className="font-medium text-sm">{label}</span>
              {isFlagged && (
                <Badge variant="destructive" className="text-xs" data-testid={`badge-${fieldKey}`}>
                  {t('provider.rejectionStatus.needsCorrection')}
                </Badge>
              )}
            </div>
            {!isFlagged && <Lock className="h-4 w-4 text-gray-400" title={t('provider.rejectionStatus.readOnly')} />}
          </div>

          {isFlagged ? (
            <div className="space-y-2">
              {currentDoc?.uploaded || currentDoc?.name ? (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>{currentDoc.name || t('provider.rejectionStatus.documentUploaded')}</span>
                </div>
              ) : (
                <p className="text-sm text-red-600">{t('provider.rejectionStatus.noDocumentUploaded')}</p>
              )}
              <label className="flex items-center gap-2 px-4 py-2 bg-white border border-dashed border-red-300 rounded-lg cursor-pointer hover:bg-red-50 transition-colors">
                <Upload className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-600">{t('provider.rejectionStatus.uploadNewDoc')}</span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => e.target.files[0] && handleDocumentUpload(docType, e.target.files[0])}
                  data-testid={`upload-${fieldKey}`}
                />
              </label>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {currentDoc?.uploaded || currentDoc?.name ? (currentDoc.name || t('provider.rejectionStatus.documentUploaded')) : t('provider.rejectionStatus.noDocument')}
            </p>
          )}
        </div>
      );
    }

    if (fieldKey === 'specializations') {
      return (
        <div
          key={fieldKey}
          className={`p-4 rounded-lg border-2 transition-all ${isFlagged ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
          data-testid={`field-${fieldKey}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {isFlagged ? <Edit2 className="h-4 w-4 text-red-600" /> : <Lock className="h-4 w-4 text-gray-400" />}
              <span className="font-medium text-sm">{label}</span>
              {isFlagged && (
                <Badge variant="destructive" className="text-xs">{t('provider.rejectionStatus.needsCorrection')}</Badge>
              )}
            </div>
          </div>

          {isFlagged ? (
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((spec) => {
                const isSelected = (currentValue || []).includes(spec.id);
                return (
                  <label
                    key={spec.id}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full border cursor-pointer transition-colors text-sm ${isSelected ? 'bg-red-100 border-red-400 text-red-800' : 'bg-white border-gray-300 hover:border-red-300'}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={isSelected}
                      onChange={() => {
                        const newValue = isSelected
                          ? (currentValue || []).filter((id) => id !== spec.id)
                          : [...(currentValue || []), spec.id];
                        handleFieldChange(fieldKey, newValue);
                      }}
                    />
                    <span>{spec.icon}</span>
                    <span>{spec.label}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(currentValue || []).map((specId) => (
                <Badge key={specId} variant="outline" className="text-xs">
                  {getSpecializationLabel(specId)}
                </Badge>
              ))}
              {(!currentValue || currentValue.length === 0) && (
                <span className="text-sm text-gray-500">{t('provider.rejectionStatus.notSpecified')}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    if (fieldKey === 'country') {
      return (
        <div
          key={fieldKey}
          className={`p-4 rounded-lg border-2 transition-all ${isFlagged ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
          data-testid={`field-${fieldKey}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {isFlagged ? <Edit2 className="h-4 w-4 text-red-600" /> : <Lock className="h-4 w-4 text-gray-400" />}
              <span className="font-medium text-sm">{label}</span>
              {isFlagged && (
                <Badge variant="destructive" className="text-xs">{t('provider.rejectionStatus.needsCorrection')}</Badge>
              )}
            </div>
          </div>

          {isFlagged ? (
            <select
              className="w-full p-2 border border-red-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-red-200 focus:border-red-400"
              value={currentValue || ''}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              data-testid={`select-${fieldKey}`}
            >
              <option value="">{t('provider.rejectionStatus.selectCountry')}</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-600">{getCountryDisplay(currentValue, t('provider.rejectionStatus.notSpecified'))}</p>
          )}
        </div>
      );
    }

    return (
      <div
        key={fieldKey}
        className={`p-4 rounded-lg border-2 transition-all ${isFlagged ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
        data-testid={`field-${fieldKey}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isFlagged ? <Edit2 className="h-4 w-4 text-red-600" /> : <Lock className="h-4 w-4 text-gray-400" />}
            <span className="font-medium text-sm">{label}</span>
            {isFlagged && (
              <Badge variant="destructive" className="text-xs">{t('provider.rejectionStatus.needsCorrection')}</Badge>
            )}
          </div>
        </div>

        {isFlagged ? (
          <Input
            value={currentValue || ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            className="bg-white border-red-300 focus:border-red-400 focus:ring-red-200"
            placeholder={t('provider.rejectionStatus.enterField', { field: label.toLowerCase() })}
            data-testid={`input-${fieldKey}`}
          />
        ) : (
          <p className="text-sm text-gray-600">
            {currentValue || t('provider.rejectionStatus.notSpecified')}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-gray-50 py-8 px-4">
      <Toaster />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="rejection-title">
            {t('provider.rejectionStatus.pageTitle')}
          </h1>
          <p className="text-gray-600">
            {t('provider.rejectionStatus.pageSubtitle')}
          </p>
        </div>

        {/* Rejection Details Card */}
        <Card className="mb-6 border-red-200" data-testid="rejection-details-card">
          <CardHeader className="bg-red-50 border-b border-red-200">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mt-1" />
              <div className="flex-1">
                <CardTitle className="text-red-800">{t('provider.rejectionStatus.reasonTitle')}</CardTitle>
                <CardDescription className="text-red-600 mt-1">
                  {t('provider.rejectionStatus.reasonSubtitle')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Reviewer-authored free text — NOT translated per §2 */}
            <p className="text-gray-800 mb-4" data-testid="rejection-reason">
              {application.rejectionReason}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{t('provider.rejectionStatus.rejectedOn', { date: formatDateLocale(application.rejectionDate || application.reviewedAt) })}</span>
              </div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>{t('provider.rejectionStatus.reviewedBy', { name: application.reviewedBy || t('provider.rejectionStatus.adminFallback') })}</span>
              </div>
              {application.reapplyCount > 0 && (
                <div className="flex items-center gap-1">
                  <History className="h-4 w-4" />
                  <span>{t('provider.rejectionStatus.previousAttempts', { n: application.reapplyCount })}</span>
                </div>
              )}
            </div>

            {flaggedFields.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  {t('provider.rejectionStatus.fieldsRequiringCorrection', { n: flaggedFields.length })}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="flagged-fields-list">
                  {flaggedFields.map((field) => (
                    <Badge key={field} variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                      {fieldLabel(field)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Application Details & Edit Form */}
        {isReapplying ? (
          <Card className="mb-6" data-testid="reapply-form">
            <CardHeader className="bg-blue-50 border-b">
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <RefreshCw className="h-5 w-5" />
                {t('provider.rejectionStatus.correctYourApplication')}
              </CardTitle>
              <CardDescription>
                <span className="text-blue-600 font-medium">{t('provider.rejectionStatus.onlyFlaggedEditable')}</span> {t('provider.rejectionStatus.onlyFlaggedEditableRest')}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {t('provider.rejectionStatus.basicInformation')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['clinicName', 'email', 'phone', 'website'].map(renderField)}
                </div>
              </div>

              {/* Location */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {t('provider.rejectionStatus.location')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['address', 'city', 'country', 'postalCode'].map(renderField)}
                </div>
              </div>

              {/* Medical Info */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {t('provider.rejectionStatus.medicalInformation')}
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {renderField('licenseNumber')}
                  {renderField('specializations')}
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('provider.rejectionStatus.documents')}
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {['businessLicense', 'medicalCertifications', 'additionalDocs'].map(renderField)}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsReapplying(false)}
                  className="flex-1"
                  disabled={isSubmitting}
                  data-testid="cancel-reapply-btn"
                >
                  {t('provider.rejectionStatus.cancel')}
                </Button>
                <Button
                  onClick={handleReapply}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={!hasChanges || isSubmitting}
                  data-testid="submit-reapplication-btn"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t('provider.rejectionStatus.submitting')}
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {t('provider.rejectionStatus.submitCorrections')}
                    </>
                  )}
                </Button>
              </div>

              {!hasChanges && (
                <p className="text-sm text-amber-600 text-center">
                  {t('provider.rejectionStatus.makeCorrectionsHint')}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6" data-testid="application-summary">
            <CardHeader>
              <CardTitle>{t('provider.rejectionStatus.applicationSummary')}</CardTitle>
              <CardDescription>
                {t('provider.rejectionStatus.applicationSummarySubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.clinicNameLabel')}</p>
                  <p className="font-medium">{application.clinicName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.emailLabel')}</p>
                  <p className="font-medium">{application.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.phoneLabel')}</p>
                  <p className="font-medium">{application.phone || t('provider.rejectionStatus.notProvided')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.locationLabel')}</p>
                  <p className="font-medium">{application.city}, {getCountryDisplay(application.country, t('provider.rejectionStatus.notSpecified'))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.licenseNumberLabel')}</p>
                  <p className={`font-medium ${flaggedFields.includes('licenseNumber') ? 'text-red-600' : ''}`}>
                    {application.licenseNumber || t('provider.rejectionStatus.notProvided')}
                    {flaggedFields.includes('licenseNumber') && (
                      <Badge variant="destructive" className="ml-2 text-xs">{t('provider.rejectionStatus.needsCorrection')}</Badge>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('provider.rejectionStatus.applicationIdLabel')}</p>
                  <p className="font-mono text-sm text-gray-600">{application.id}</p>
                </div>
              </div>

              {/* Specializations */}
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('provider.rejectionStatus.specializationsLabel')}</p>
                <div className="flex flex-wrap gap-1">
                  {(application.specializations || []).map((specId) => (
                    <Badge key={specId} variant="outline" className="text-xs">
                      {getSpecializationLabel(specId)}
                    </Badge>
                  ))}
                  {(!application.specializations || application.specializations.length === 0) && (
                    <span className="text-sm text-gray-500">{t('provider.rejectionStatus.noneSelected')}</span>
                  )}
                </div>
              </div>

              {/* Reapply CTA */}
              <div className="pt-4 border-t">
                <Button
                  onClick={() => setIsReapplying(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="start-reapply-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('provider.rejectionStatus.reapplyCta')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="text-center text-sm text-gray-500">
          <p>
            {t('provider.rejectionStatus.helpPrefix')}{' '}
            <a href="mailto:support@treatx.health" className="text-blue-600 hover:underline">
              support@treatx.health
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const RejectionStatusPage = () => {
  return (
    <ClinicApplicationProvider>
      <RejectionStatusPageInner />
    </ClinicApplicationProvider>
  );
};

export default RejectionStatusPage;
